
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchShoppingItems } from '@/services/shopping/items/index';
import { networkService } from '@/services/network.service';
import { shoppingItemRepository } from "@/repositories/shoppingItem.repository";
import { offlineDb } from '@/db/offlineDb';
import OfflineLogger from '@/utils/offlineLogger';
import { ShoppingItem, ShoppingItemWithProduct } from "@/types";
import { shouldApplyRemoteUpsert } from './useShoppingItemsSubscription';
import { queryClient } from '@/services/queryClient';

// Helper function to convert SyncableShoppingItem to ShoppingItem
const convertToShoppingItem = (syncableItem: any): ShoppingItem => ({
  id: syncableItem.id,
  list_id: syncableItem.list_id,
  product_id: syncableItem.product_id,
  quantity: syncableItem.quantity || '1',
  unit: syncableItem.unit,
  category: syncableItem.category,
  checked: syncableItem.checked || false,
  position: syncableItem.position,
  attributes: syncableItem.attributes || [],
  created_at: typeof syncableItem.created_at === 'string' 
    ? new Date(syncableItem.created_at) 
    : syncableItem.created_at || new Date(),
  user_id: syncableItem.user_id
});

export function useOfflineShoppingItems(listId: string) {
  const [isOnline, setIsOnline] = useState(networkService.isOnline());
  
  // Update online status when network changes
  useEffect(() => {
    const handleNetworkChange = (status: { connected: boolean }) => {
      setIsOnline(status.connected);
    };
    
    const unsubscribe = networkService.addListener(handleNetworkChange);
    return () => unsubscribe();
  }, []);
  
  // ✅ FIX: Migliorate le strategie di fetch e sync per shopping items con logging dettagliato
  const { data: shoppingItems, isLoading, error, refetch } = useQuery<ShoppingItemWithProduct[]>({
    queryKey: ['shopping-items', listId],
    queryFn: async () => {
      if (networkService.isOnline()) {
        try {
          //OfflineLogger.info('useOfflineShoppingItems', `Fetching shopping items online for list ${listId}`);
          const onlineItems = await fetchShoppingItems(listId);

          await Promise.all(
            onlineItems.map(async item => {
              try {
                // pending-local-wins: non sovrascrivere un edit locale non ancora sincronizzato
                const existing = await offlineDb.shoppingItems.get(item.id);
                if (shouldApplyRemoteUpsert(existing, undefined)) {
                  await offlineDb.shoppingItems.put({
                    ...item,
                    created_at: item.created_at instanceof Date
                      ? item.created_at.toISOString()
                      : new Date(item.created_at || Date.now()).toISOString(),
                    attributes: item.attributes || [],
                    _syncStatus: 'synced' as const,
                    _lastModified: Date.now()
                  });
                }
                // I prodotti si aggiornano sempre: non sono modificabili localmente
                if (item.product) {
                  await offlineDb.products.put({
                    id: item.product.id,
                    user_id: item.product.userId,
                    name: item.product.name,
                    category_id: item.product.categoryId,
                    default_quantity: item.product.defaultQuantity,
                    default_unit: item.product.defaultUnit,
                    photo: item.product.photo,
                    barcode: item.product.barcode,
                    openfoodfacts_id: item.product.openfoodfacts_id,
                    attributes: item.product.attributes || [],
                    created_at: item.product.createdAt,
                    updated_at: item.product.createdAt,
                    _syncStatus: 'synced',
                    _lastModified: Date.now()
                  });
                }
              } catch (err) {
                OfflineLogger.error('useOfflineShoppingItems.cacheItem', `Error caching item ${item.id}: ${err}`);
              }
            })
          );

          // Aggiorna i contatori delle liste (query diverse da shopping-items) dopo il fetch online
          await queryClient.invalidateQueries({ queryKey: ['shopping-list', listId] });
          await queryClient.invalidateQueries({ queryKey: ['enhanced-primary-list'] });

          return onlineItems;
        } catch (error) {
          OfflineLogger.error('useOfflineShoppingItems.fetchOnline', error);
          return await shoppingItemRepository.getWithProductsByListId(listId);
        }
      } else {
        //OfflineLogger.info('useOfflineShoppingItems', `Using offline shopping items from local DB for list ${listId}`);
        return await shoppingItemRepository.getWithProductsByListId(listId);
      }
    },
    staleTime: 0,
    enabled: !!listId,
  });

  return {
    shoppingItems: shoppingItems || [],
    isLoading,
    error,
    isOnline,
    refetch
  };
}
